import { useState, useEffect, useRef } from "react";
import { uid, T, mono } from "./utils";
import { supabase } from "./supabase";

const UNGROUPED = "__none__";

function DocRow({ doc, onDescChange, onDelete, onOpen, onRename, onDragStart, onDragEnd, onDragOverRow, onDropRow, isDragging, isDragOverTarget, expanded, onToggleExpand }) {
  const [desc, setDesc] = useState(doc.description || "");
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(doc.name);
  const timer = useRef();

  useEffect(() => setNameVal(doc.name), [doc.name]);

  const saveDesc = (v) => {
    setDesc(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onDescChange(doc.id, v), 500);
  };

  const commitName = () => {
    setEditingName(false);
    if (nameVal.trim() && nameVal.trim() !== doc.name) onRename(doc.id, nameVal.trim());
  };

  return (
    <div
      draggable
      onDragStart={() => onDragStart(doc.id)}
      onDragEnd={onDragEnd}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); onDragOverRow(doc.id); }}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); onDropRow(doc.id); }}
      style={{ borderBottom: `1px solid ${T.borderLight}`, background: "#fff", opacity: isDragging ? 0.3 : 1, borderTop: isDragOverTarget ? `2px solid ${T.accent}` : "2px solid transparent" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "grab" }}>
        <span style={{ ...mono, fontSize: 10, color: T.border, flexShrink: 0 }}>⠿</span>
        <span style={{ fontSize: 16, color: T.mute, flexShrink: 0 }}>📄</span>
        {editingName ? (
          <input value={nameVal} onChange={e => setNameVal(e.target.value)} onBlur={commitName}
            onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameVal(doc.name); setEditingName(false); } }}
            autoFocus onClick={e => e.stopPropagation()}
            style={{ fontSize: 13, fontWeight: 600, color: T.ink, border: `1px solid ${T.accent}`, borderRadius: 2, padding: "2px 6px", fontFamily: "'Inter',sans-serif", outline: "none", minWidth: 200, maxWidth: 260 }} />
        ) : (
          <span onClick={onToggleExpand} onDoubleClick={e => { e.stopPropagation(); setEditingName(true); }}
            title="Açmak için tıkla, yeniden adlandırmak için çift tıkla"
            style={{ fontSize: 13, fontWeight: 600, color: T.ink, cursor: "pointer", flexShrink: 0, minWidth: 200, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {doc.name}
          </span>
        )}
        {!expanded && doc.description && (
          <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.border, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{doc.description}</span>
        )}
        <span style={{ flex: 1 }} />
        <span onClick={onToggleExpand} style={{ ...mono, fontSize: 9, color: T.border, cursor: "pointer", flexShrink: 0 }}>{expanded ? "▲" : "▼"}</span>
        <button onClick={() => onDelete(doc.id, doc.file_path)}
          style={{ background: "none", border: "none", color: T.mute, fontSize: 14, cursor: "pointer", padding: 0, opacity: 0.4, flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.4"}>×</button>
      </div>
      {expanded && (
        <div style={{ padding: "0 16px 14px 46px", display: "flex", alignItems: "center", gap: 10 }}>
          <input value={desc} onChange={e => saveDesc(e.target.value)} placeholder="Bu döküman ne için kullanılıyor…"
            style={{ flex: 1, minWidth: 0, border: `1px solid ${T.border}`, borderRadius: 2, background: T.bg, fontSize: 13, color: T.ink, fontFamily: "'Inter',sans-serif", outline: "none", padding: "6px 10px" }} />
          <button onClick={onOpen} style={{ ...mono, fontSize: 9, background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "6px 12px", cursor: "pointer", flexShrink: 0 }}>Aç ↗</button>
        </div>
      )}
    </div>
  );
}

function GroupSection({ groupName, docs, isUngrouped, onDescChange, onDelete, onOpen, onRename, onDragStart, onDragEnd, draggingId, onDropOnGroup, onRenameGroup, onDeleteGroup, expandedId, onToggleExpand, dragOverRowId, onDragOverRow, onDropRow, isCollapsed, onToggleCollapse }) {
  const [isOver, setIsOver] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(groupName);

  const commitName = () => {
    setEditingName(false);
    if (nameVal.trim() && nameVal.trim() !== groupName) onRenameGroup(groupName, nameVal.trim());
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", padding: isCollapsed ? "4px 6px" : 0, marginLeft: isCollapsed ? -6 : 0, borderRadius: 4, background: isCollapsed && isOver ? "#f5f3ff" : "transparent", border: isCollapsed ? `1px solid ${isOver ? T.accent : "transparent"}` : "none" }}
        onClick={onToggleCollapse}
        onDragOver={e => { if (isCollapsed) { e.preventDefault(); setIsOver(true); } }}
        onDragLeave={() => setIsOver(false)}
        onDrop={e => { if (isCollapsed) { e.preventDefault(); setIsOver(false); onDropOnGroup(isUngrouped ? UNGROUPED : groupName); } }}>
        <span style={{ ...mono, fontSize: 9, color: T.border, flexShrink: 0 }}>{isCollapsed ? "▶" : "▼"}</span>
        {isUngrouped ? (
          <span style={{ ...mono, fontSize: 10, color: T.border, letterSpacing: "0.1em" }}>GRUPSUZ</span>
        ) : editingName ? (
          <input value={nameVal} onChange={e => setNameVal(e.target.value)} onBlur={commitName}
            onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameVal(groupName); setEditingName(false); } }}
            autoFocus onClick={e => e.stopPropagation()}
            style={{ ...mono, fontSize: 11, fontWeight: 600, border: `1px solid ${T.accent}`, borderRadius: 2, padding: "1px 6px", color: T.ink, outline: "none" }} />
        ) : (
          <span onDoubleClick={e => { e.stopPropagation(); setEditingName(true); }} title="Aç/kapa; yeniden adlandırmak için çift tıkla"
            style={{ ...mono, fontSize: 11, fontWeight: 600, color: T.ink, letterSpacing: "0.05em", cursor: "pointer" }}>{groupName}</span>
        )}
        <span style={{ ...mono, fontSize: 9, color: T.border }}>{docs.length}</span>
        {!isUngrouped && (
          <button onClick={e => { e.stopPropagation(); onDeleteGroup(groupName); }}
            style={{ ...mono, fontSize: 9, background: "none", border: "none", color: T.border, cursor: "pointer", padding: 0, marginLeft: 4 }}>
            grubu kaldır
          </button>
        )}
      </div>
      {!isCollapsed && (
      <div
        onDragOver={e => { e.preventDefault(); setIsOver(true); }}
        onDragLeave={() => setIsOver(false)}
        onDrop={e => { e.preventDefault(); setIsOver(false); onDropOnGroup(isUngrouped ? UNGROUPED : groupName); }}
        style={{ border: `1px solid ${isOver ? T.accent : T.border}`, borderRadius: 8, overflow: "hidden", background: isOver ? "#f5f3ff" : "transparent", minHeight: docs.length === 0 ? 44 : 0, transition: "all .1s" }}>
        {docs.length === 0 ? (
          <div style={{ ...mono, fontSize: 9, color: T.border, padding: "12px 16px" }}>Buraya sürükle</div>
        ) : docs.map(doc => (
          <DocRow key={doc.id} doc={doc} onDescChange={onDescChange} onDelete={onDelete} onOpen={() => onOpen(doc.file_path)}
            onRename={onRename}
            onDragStart={onDragStart} onDragEnd={onDragEnd} isDragging={draggingId === doc.id}
            expanded={expandedId === doc.id} onToggleExpand={() => onToggleExpand(doc.id)}
            isDragOverTarget={dragOverRowId === doc.id} onDragOverRow={onDragOverRow} onDropRow={onDropRow} />
        ))}
      </div>
      )}
    </div>
  );
}

export function InfoView() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(null); // null = henüz başlatılmadı
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [groups, setGroups] = useState([]);
  const fileRef = useRef();

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (collapsedGroups === null && !loading) {
      const allNames = [...new Set([...groups.map(g => g.name), ...docs.map(d => d.group_name).filter(Boolean)])];
      setCollapsedGroups(new Set([...allNames, "GRUPSUZ"]));
    }
  }, [loading, groups, docs]); // eslint-disable-line

  function toggleGroupCollapse(name) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  async function load() {
    setLoading(true);
    const [{ data: docData }, { data: groupData }] = await Promise.all([
      supabase.from("info_documents").select("*").order("sort_order", { ascending: true, nullsFirst: false }).order("created_at"),
      supabase.from("info_groups").select("*").order("created_at"),
    ]);
    setDocs(docData || []);
    setGroups(groupData || []);
    setLoading(false);
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const path = `${uid()}-${file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_")}`;
    const { error: upErr } = await supabase.storage.from("info-documents").upload(path, file);
    if (upErr) {
      alert("Yükleme hatası: " + upErr.message);
      setUploading(false);
      e.target.value = "";
      return;
    }
    const row = { id: uid(), name: file.name, file_path: path, description: "", group_name: null, sort_order: docs.length };
    setDocs(prev => [...prev, row]);
    await supabase.from("info_documents").insert(row);
    setUploading(false);
    e.target.value = "";
  }

  async function updateDescription(id, description) {
    await supabase.from("info_documents").update({ description }).eq("id", id);
  }

  async function renameDoc(id, name) {
    setDocs(prev => prev.map(d => d.id === id ? { ...d, name } : d));
    await supabase.from("info_documents").update({ name }).eq("id", id);
  }

  async function deleteDoc(id, filePath) {
    if (!window.confirm("Bu dökümanı silmek istediğinize emin misiniz?")) return;
    setDocs(prev => prev.filter(d => d.id !== id));
    if (filePath) await supabase.storage.from("info-documents").remove([filePath]);
    await supabase.from("info_documents").delete().eq("id", id);
  }

  function openDoc(filePath) {
    const { data } = supabase.storage.from("info-documents").getPublicUrl(filePath);
    if (data?.publicUrl) window.open(data.publicUrl, "_blank");
  }

  async function persistOrder(list) {
    // Tek toplu upsert — önceden her belge için ayrı istek atılıyordu.
    const rows = list.map((d, i) => ({ id: d.id, name: d.name, file_path: d.file_path, sort_order: i }));
    await supabase.from("info_documents").upsert(rows, { onConflict: "id" });
  }

  // Aynı grup içinde sürükle-bırak ile sıralama
  function handleDragOverRow(targetId) {
    if (!draggingId || draggingId === targetId) return;
    setDragOverRowId(targetId);
  }

  function handleDropRow(targetId) {
    if (!draggingId || draggingId === targetId) { setDragOverRowId(null); return; }
    const dragged = docs.find(d => d.id === draggingId);
    const target = docs.find(d => d.id === targetId);
    if (!dragged || !target) { setDragOverRowId(null); return; }
    // Sadece aynı grup içinde yeniden sıralama yapılır
    if ((dragged.group_name || null) !== (target.group_name || null)) { setDragOverRowId(null); return; }

    setDocs(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(d => d.id === draggingId);
      const toIdx = next.findIndex(d => d.id === targetId);
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragged);
      persistOrder(next);
      return next;
    });
    setDragOverRowId(null);
    setDraggingId(null);
  }

  async function moveToGroup(groupKey) {
    if (!draggingId) return;
    const groupName = groupKey === UNGROUPED ? null : groupKey;
    setDocs(prev => prev.map(d => d.id === draggingId ? { ...d, group_name: groupName } : d));
    await supabase.from("info_documents").update({ group_name: groupName }).eq("id", draggingId);
    setDraggingId(null);
    setDragOverRowId(null);
  }

  async function renameGroup(oldName, newName) {
    setDocs(prev => prev.map(d => d.group_name === oldName ? { ...d, group_name: newName } : d));
    await supabase.from("info_documents").update({ group_name: newName }).eq("group_name", oldName);
    const g = groups.find(g => g.name === oldName);
    if (g) {
      setGroups(prev => prev.map(x => x.id === g.id ? { ...x, name: newName } : x));
      await supabase.from("info_groups").update({ name: newName }).eq("id", g.id);
    }
  }

  async function deleteGroup(groupName) {
    if (!window.confirm(`"${groupName}" grubunu kaldır? İçindeki dökümanlar gruplandırılmamış listeye taşınacak.`)) return;
    setDocs(prev => prev.map(d => d.group_name === groupName ? { ...d, group_name: null } : d));
    await supabase.from("info_documents").update({ group_name: null }).eq("group_name", groupName);
    const g = groups.find(g => g.name === groupName);
    if (g) {
      setGroups(prev => prev.filter(x => x.id !== g.id));
      await supabase.from("info_groups").delete().eq("id", g.id);
    }
  }

  async function addGroup() {
    const name = newGroupName.trim();
    if (!name) return;
    const row = { id: uid(), name };
    setGroups(prev => [...prev, row]);
    await supabase.from("info_groups").insert(row);
    setNewGroupName("");
    setShowNewGroup(false);
  }

  if (loading) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ ...mono, color: T.mute }}>Loading</span></div>;

  const realDocs = docs;
  const groupNames = [...new Set([...groups.map(g => g.name), ...docs.map(d => d.group_name).filter(Boolean)])];
  const ungroupedDocs = realDocs.filter(d => !d.group_name);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "32px 40px" }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 18 }}>
          <div style={{ ...mono, fontSize: 9, color: T.mute, letterSpacing: "0.15em" }}>DÖKÜMANLAR</div>
          {!showNewGroup ? (
            <button onClick={() => setShowNewGroup(true)} style={{ ...mono, fontSize: 9, background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 10px", cursor: "pointer", color: T.mute }}>+ Grup Oluştur</button>
          ) : (
            <div style={{ display: "flex", gap: 6 }}>
              <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Grup adı…" autoFocus
                onKeyDown={e => { if (e.key === "Enter") addGroup(); if (e.key === "Escape") setShowNewGroup(false); }}
                style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 8px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none" }} />
              <button onClick={addGroup} style={{ ...mono, fontSize: 9, background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "4px 10px", cursor: "pointer" }}>Ekle</button>
            </div>
          )}
        </div>

        {realDocs.length === 0 ? (
          <div style={{ ...mono, fontSize: 10, color: T.mute, padding: "20px 0" }}>Henüz döküman yok.</div>
        ) : (
          <>
            {groupNames.map(gName => (
              <GroupSection key={gName} groupName={gName} docs={realDocs.filter(d => d.group_name === gName)}
                isCollapsed={collapsedGroups ? collapsedGroups.has(gName) : true} onToggleCollapse={() => toggleGroupCollapse(gName)}
                onDescChange={updateDescription} onDelete={deleteDoc} onOpen={openDoc} onRename={renameDoc}
                onDragStart={setDraggingId} onDragEnd={() => { setDraggingId(null); setDragOverRowId(null); }}
                draggingId={draggingId} onDropOnGroup={moveToGroup}
                onRenameGroup={renameGroup} onDeleteGroup={deleteGroup}
                expandedId={expandedId} onToggleExpand={id => setExpandedId(prev => prev === id ? null : id)}
                dragOverRowId={dragOverRowId} onDragOverRow={handleDragOverRow} onDropRow={handleDropRow} />
            ))}
            <GroupSection groupName="GRUPSUZ" isUngrouped docs={ungroupedDocs}
              isCollapsed={collapsedGroups ? collapsedGroups.has("GRUPSUZ") : true} onToggleCollapse={() => toggleGroupCollapse("GRUPSUZ")}
              onDescChange={updateDescription} onDelete={deleteDoc} onOpen={openDoc} onRename={renameDoc}
              onDragStart={setDraggingId} onDragEnd={() => { setDraggingId(null); setDragOverRowId(null); }}
              draggingId={draggingId} onDropOnGroup={moveToGroup}
              expandedId={expandedId} onToggleExpand={id => setExpandedId(prev => prev === id ? null : id)}
              dragOverRowId={dragOverRowId} onDragOverRow={handleDragOverRow} onDropRow={handleDropRow} />
          </>
        )}

        <button onClick={() => fileRef.current.click()} disabled={uploading}
          style={{ ...mono, fontSize: 11, background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 18px", cursor: uploading ? "wait" : "pointer", color: T.mute, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>⬆</span> {uploading ? "Yükleniyor…" : "Döküman Yükle"}
        </button>
        <input ref={fileRef} type="file" onChange={handleUpload} style={{ display: "none" }} />
      </div>
    </div>
  );
}
